/**
 * PageLayout Component
 * Provides consistent page structure and background styling across the app.
 * Supports App Mode with safe-area insets for mobile devices.
 */

import React from 'react';
import { AnimatedBackground } from '@/components/ui';
import { TrialBanner } from '@/components/TrialBanner';
import { useLayout } from '@/contexts/LayoutContext';
import { Chrome } from '@/components/layout';

type PageVariant = 'default' | 'minimal' | 'plain';

interface PageLayoutProps {
  children: React.ReactNode;
  className?: string;
  /**
   * Page variant for different styling needs:
   * - default: Subtle gradient background (most pages)
   * - minimal: Clean white background (auth pages, modals)
   * - plain: No background styling (full custom control)
   */
  variant?: PageVariant;
}

const variantStyles: Record<PageVariant, string> = {
  default: 'bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 min-h-screen',
  minimal: 'bg-white min-h-screen',
  plain: 'min-h-screen',
};

const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  className = '',
  variant = 'default',
}) => {
  const { styles } = useLayout();

  // Use background variant from layout config, but allow page-level variant to override
  const effectiveVariant = variant;

  // Add safe-area padding when enabled in layout config
  const safeAreaStyles = styles.safeAreaInsets
    ? 'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]'
    : '';

  return (
    <div className={`relative ${variantStyles[effectiveVariant]} ${safeAreaStyles} ${className}`}>
      <Chrome component="animatedBackground">
        {effectiveVariant === 'default' && (
          <AnimatedBackground variant="light" intensity="subtle" />
        )}
      </Chrome>
      <Chrome component="trialBanner">
        <TrialBanner />
      </Chrome>
      <div className="relative">
        {children}
      </div>
    </div>
  );
};

export default PageLayout;
